import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Radio, Wifi, WifiOff, Send, MapPin, Heart, HandHelping,
  Loader2, X, ChevronDown, ChevronUp, AlertTriangle, Bluetooth,
  QrCode, Users, Signal, Clock, ArrowUp
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// IndexedDB helpers for offline message queue
const DB_NAME = 'crisis-nearby';
const STORE_NAME = 'messages';

function openNearbyDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('synced', 'synced', { unique: false });
        store.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function queueOfflineMessage(msg) {
  const db = await openNearbyDB();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  tx.objectStore(STORE_NAME).put(msg);
  return new Promise((resolve, reject) => {
    tx.oncomplete = resolve;
    tx.onerror = () => reject(tx.error);
  });
}

async function getQueuedMessages() {
  const db = await openNearbyDB();
  const tx = db.transaction(STORE_NAME, 'readonly');
  const req = tx.objectStore(STORE_NAME).getAll();
  return new Promise((resolve) => {
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => resolve([]);
  });
}

async function markSynced(ids) {
  const db = await openNearbyDB();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  const store = tx.objectStore(STORE_NAME);
  for (const id of ids) {
    const getReq = store.get(id);
    getReq.onsuccess = () => {
      if (getReq.result) {
        getReq.result.synced = true;
        store.put(getReq.result);
      }
    };
  }
}

// Radar animation component
function RadarPulse({ color, nearbyCount }) {
  return (
    <div className="relative w-48 h-48 mx-auto my-4">
      {/* Outer rings */}
      {[0, 1, 2].map(i => (
        <div
          key={i}
          className="absolute inset-0 rounded-full border opacity-20"
          style={{
            borderColor: color,
            animation: `radar-pulse 3s ease-out ${i * 1}s infinite`,
          }}
        />
      ))}
      {/* Center dot */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full" style={{ backgroundColor: color, boxShadow: `0 0 20px ${color}` }} />
      {/* Sweep line */}
      <div
        className="absolute top-1/2 left-1/2 w-[1px] h-24 origin-bottom"
        style={{
          backgroundColor: color,
          opacity: 0.4,
          animation: 'radar-sweep 3s linear infinite',
          transformOrigin: 'bottom center',
        }}
      />
      {/* Nearby count */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-8 text-center">
        <span className="text-3xl font-bold" style={{ color }}>{nearbyCount}</span>
        <p className="text-[10px] text-muted-foreground mt-0.5">nearby</p>
      </div>
      {/* Nearby dots */}
      {Array.from({ length: Math.min(nearbyCount, 8) }).map((_, i) => {
        const angle = (i / Math.max(nearbyCount, 8)) * Math.PI * 2 + 0.3;
        const r = 50 + Math.random() * 30;
        const x = Math.cos(angle) * r;
        const y = Math.sin(angle) * r;
        return (
          <div
            key={i}
            className="absolute w-2.5 h-2.5 rounded-full"
            style={{
              backgroundColor: color,
              opacity: 0.6 + Math.random() * 0.4,
              top: `calc(50% + ${y}px)`,
              left: `calc(50% + ${x}px)`,
              animation: `beacon-blink 2s ease-in-out ${i * 0.3}s infinite`,
            }}
          />
        );
      })}
    </div>
  );
}

// Single nearby message card
function NearbyMessageCard({ msg, color, onNavigate }) {
  const typeIcon = msg.type === 'need' ? <AlertTriangle className="w-3.5 h-3.5" /> : <Heart className="w-3.5 h-3.5" />;
  const typeColor = msg.type === 'need' ? '#ef4444' : '#22c55e';
  const distanceText = msg.distance != null
    ? msg.distance < 1 ? `${Math.round(msg.distance * 1000)}m away` : `${msg.distance.toFixed(1)}km away`
    : 'Unknown distance';
  const timeAgo = getTimeAgo(msg.timestamp);

  return (
    <div className="bg-card/60 border border-border/40 rounded-lg p-3 backdrop-blur-sm" data-testid={`nearby-msg-${msg.id}`}>
      <div className="flex items-start gap-2">
        <div className="p-1.5 rounded-md" style={{ backgroundColor: `${typeColor}20`, color: typeColor }}>
          {typeIcon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <Badge className="text-[9px] px-1.5 py-0" style={{ backgroundColor: typeColor, color: '#fff' }}>
              {msg.type === 'need' ? 'NEED' : 'OFFER'}
            </Badge>
            <span className="text-[10px] text-muted-foreground">{timeAgo}</span>
            {!msg.synced && <Badge variant="outline" className="text-[9px] px-1 py-0 border-amber-500/40 text-amber-400">queued</Badge>}
          </div>
          <p className="text-xs font-medium text-foreground truncate">{msg.message}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
              <MapPin className="w-2.5 h-2.5" /> {distanceText}
            </span>
            {msg.category && (
              <span className="text-[10px] text-muted-foreground">{msg.category}</span>
            )}
          </div>
        </div>
        {msg.lat && msg.lng && (
          <button
            onClick={() => onNavigate?.({ lat: msg.lat, lng: msg.lng, name: msg.message })}
            className="p-1.5 rounded-md hover:bg-card/80 transition-colors"
            style={{ color }}
            data-testid={`navigate-to-${msg.id}`}
          >
            <ArrowUp className="w-3.5 h-3.5 rotate-45" />
          </button>
        )}
      </div>
    </div>
  );
}

function getTimeAgo(ts) {
  const diff = Date.now() - new Date(ts).getTime();
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return `${Math.floor(diff / 86400000)}d ago`;
}

// Main component
export default function NearbyBeacon({ userLocation, isOffline, color, onNavigate, userName }) {
  const [active, setActive] = useState(false);
  const [broadcasting, setBroadcasting] = useState(false);
  const [nearbyMessages, setNearbyMessages] = useState([]);
  const [queuedMessages, setQueuedMessages] = useState([]);
  const [nearbyCount, setNearbyCount] = useState(0);
  const [showCompose, setShowCompose] = useState(false);
  const [composeType, setComposeType] = useState('need');
  const [composeText, setComposeText] = useState('');
  const [composeCategory, setComposeCategory] = useState('');
  const [sending, setSending] = useState(false);
  const [showExpanded, setShowExpanded] = useState(true);
  const [bleStatus, setBleStatus] = useState('idle'); // idle | scanning | connected | unsupported
  const pollRef = useRef(null);
  const syncRef = useRef(null);

  const c1 = color || '#a855f7';

  // Activate the nearby network
  const activate = useCallback(async () => {
    setActive(true);
    setBroadcasting(true);

    // Load queued offline messages
    try {
      const queued = await getQueuedMessages();
      setQueuedMessages(queued.filter(m => !m.synced));
    } catch { /* no queue */ }

    // Check BLE support
    if (navigator.bluetooth) {
      setBleStatus('idle');
    } else {
      setBleStatus('unsupported');
    }

    // Register presence with server (if online)
    if (!isOffline && userLocation) {
      try {
        await fetch(`${API}/api/crisis/nearby/beacon`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            lat: userLocation.lat,
            lng: userLocation.lng,
            name: userName || 'Anonymous',
          }),
        });
      } catch { /* offline — no problem */ }
    }
  }, [isOffline, userLocation, userName]);

  // Poll for nearby messages (when online)
  useEffect(() => {
    if (!active || isOffline || !userLocation) return;

    const poll = async () => {
      try {
        const res = await fetch(
          `${API}/api/crisis/nearby/messages?lat=${userLocation.lat}&lng=${userLocation.lng}&radius_km=2`
        );
        if (res.ok) {
          const data = await res.json();
          setNearbyMessages(data.messages || []);
          setNearbyCount(data.nearby_count || 0);
        }
      } catch { /* offline now */ }
    };

    poll();
    pollRef.current = setInterval(poll, 10000); // Poll every 10s
    return () => clearInterval(pollRef.current);
  }, [active, isOffline, userLocation]);

  // Auto-sync queued messages when coming back online
  useEffect(() => {
    if (!active || isOffline || !userLocation) return;

    const syncQueued = async () => {
      const queued = await getQueuedMessages();
      const unsynced = queued.filter(m => !m.synced);
      if (unsynced.length === 0) return;

      const syncedIds = [];
      for (const msg of unsynced) {
        try {
          const res = await fetch(`${API}/api/crisis/nearby/messages`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(msg),
          });
          if (res.ok) syncedIds.push(msg.id);
        } catch { break; }
      }

      if (syncedIds.length > 0) {
        await markSynced(syncedIds);
        toast.success(`Synced ${syncedIds.length} queued message(s)`);
        setQueuedMessages(prev => prev.filter(m => !syncedIds.includes(m.id)));
      }
    };

    syncQueued();
    syncRef.current = setInterval(syncQueued, 30000);
    return () => clearInterval(syncRef.current);
  }, [active, isOffline, userLocation]);

  // Send / queue a message
  const handleSend = useCallback(async () => {
    if (!composeText.trim()) return;
    setSending(true);

    const msg = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      type: composeType,
      message: composeText.trim(),
      category: composeCategory || undefined,
      lat: userLocation?.lat,
      lng: userLocation?.lng,
      timestamp: new Date().toISOString(),
      synced: false,
      sender: userName || 'Anonymous',
    };

    // Try to send online first
    if (!isOffline) {
      try {
        const res = await fetch(`${API}/api/crisis/nearby/messages`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(msg),
        });
        if (res.ok) {
          msg.synced = true;
          toast.success('Message broadcast to nearby users');
        }
      } catch { /* failed — will queue */ }
    }

    // Queue in IndexedDB (synced or not)
    await queueOfflineMessage(msg);

    if (!msg.synced) {
      toast.info('Message queued — will broadcast when connection returns');
      setQueuedMessages(prev => [...prev, msg]);
    }

    setComposeText('');
    setComposeCategory('');
    setShowCompose(false);
    setSending(false);
  }, [composeType, composeText, composeCategory, userLocation, isOffline, userName]);

  // Deactivate
  const deactivate = () => {
    setActive(false);
    setBroadcasting(false);
    clearInterval(pollRef.current);
    clearInterval(syncRef.current);
    setNearbyMessages([]);
    setNearbyCount(0);
  };

  // BLE scan for mesh devices
  const scanForMeshDevices = async () => {
    if (!navigator.bluetooth) {
      toast.error('Bluetooth not supported on this browser');
      return;
    }
    setBleStatus('scanning');
    try {
      const device = await navigator.bluetooth.requestDevice({
        filters: [
          { namePrefix: 'Meshtastic' },
          { namePrefix: 'LORA' },
          { namePrefix: 'Crisis' },
        ],
        optionalServices: ['6e400001-b5a3-f393-e0a9-e50e24dcca9e'], // Nordic UART
      });
      setBleStatus('connected');
      toast.success(`Connected to ${device.name || 'mesh device'}`);
      // TODO: Implement GATT communication for message relay
    } catch (err) {
      setBleStatus('idle');
      if (err.name !== 'NotFoundError') {
        toast.error('Bluetooth scan cancelled');
      }
    }
  };

  const allMessages = [
    ...nearbyMessages,
    ...queuedMessages.filter(q => !nearbyMessages.find(n => n.id === q.id)),
  ].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  if (!active) {
    return (
      <button
        onClick={activate}
        className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border/40 bg-card/60 backdrop-blur-sm hover:bg-card/80 transition-all w-full"
        data-testid="activate-nearby-btn"
      >
        <div className="p-1.5 rounded-lg" style={{ backgroundColor: `${c1}20` }}>
          <Radio className="w-4 h-4" style={{ color: c1 }} />
        </div>
        <div className="text-left flex-1">
          <p className="text-sm font-semibold text-foreground">Nearby Crisis Network</p>
          <p className="text-[10px] text-muted-foreground">Share & receive help from people nearby — works offline</p>
        </div>
        <Signal className="w-4 h-4 text-muted-foreground" />
      </button>
    );
  }

  return (
    <div className="rounded-xl border border-border/40 bg-card/80 backdrop-blur-sm overflow-hidden" data-testid="nearby-beacon-panel">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-border/30">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Radio className="w-4 h-4" style={{ color: c1 }} />
            {broadcasting && (
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            )}
          </div>
          <span className="text-sm font-semibold text-foreground">Nearby Network</span>
          <Badge className="text-[9px] px-1.5 py-0" style={{ backgroundColor: isOffline ? '#f59e0b' : '#22c55e', color: '#fff' }}>
            {isOffline ? 'OFFLINE' : 'LIVE'}
          </Badge>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setShowExpanded(!showExpanded)} className="p-1 rounded hover:bg-background/50">
            {showExpanded ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />}
          </button>
          <button onClick={deactivate} className="p-1 rounded hover:bg-background/50" data-testid="close-nearby-btn">
            <X className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
        </div>
      </div>

      {showExpanded && (
        <div className="p-3 space-y-3">
          {/* Radar */}
          <RadarPulse color={c1} nearbyCount={nearbyCount + queuedMessages.length} />

          {/* Status bar */}
          <div className="flex items-center justify-center gap-3 text-[10px] text-muted-foreground">
            <span className="flex items-center gap-1">
              {isOffline ? <WifiOff className="w-3 h-3" /> : <Wifi className="w-3 h-3 text-green-500" />}
              {isOffline ? 'Queuing locally' : 'Broadcasting'}
            </span>
            <span className="flex items-center gap-1">
              <Users className="w-3 h-3" /> {nearbyCount} nearby
            </span>
            {queuedMessages.length > 0 && (
              <span className="flex items-center gap-1 text-amber-400">
                <Clock className="w-3 h-3" /> {queuedMessages.length} queued
              </span>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex gap-2">
            <Button
              size="sm"
              className="flex-1 h-8 text-xs text-white"
              style={{ backgroundColor: '#ef4444' }}
              onClick={() => { setShowCompose(true); setComposeType('need'); }}
              data-testid="post-need-btn"
            >
              <AlertTriangle className="w-3 h-3 mr-1" /> I Need Help
            </Button>
            <Button
              size="sm"
              className="flex-1 h-8 text-xs text-white"
              style={{ backgroundColor: '#22c55e' }}
              onClick={() => { setShowCompose(true); setComposeType('offer'); }}
              data-testid="post-offer-btn"
            >
              <HandHelping className="w-3 h-3 mr-1" /> I Can Help
            </Button>
          </div>

          {/* Mesh device connection */}
          {navigator.bluetooth && (
            <button
              onClick={scanForMeshDevices}
              className="flex items-center gap-2 w-full px-3 py-2 rounded-lg border border-border/30 bg-background/30 hover:bg-background/50 transition-colors text-xs"
              data-testid="ble-scan-btn"
            >
              <Bluetooth className="w-3.5 h-3.5" style={{ color: bleStatus === 'connected' ? '#22c55e' : c1 }} />
              <span className="text-muted-foreground flex-1 text-left">
                {bleStatus === 'scanning' ? 'Scanning for mesh radios...' :
                 bleStatus === 'connected' ? 'Connected to mesh radio' :
                 'Connect to mesh radio (Meshtastic/LoRa)'}
              </span>
              {bleStatus === 'scanning' && <Loader2 className="w-3 h-3 animate-spin" />}
              {bleStatus === 'connected' && <Signal className="w-3 h-3 text-green-500" />}
            </button>
          )}

          {/* Compose panel */}
          {showCompose && (
            <div className="bg-background/40 border border-border/30 rounded-lg p-3 space-y-2" data-testid="compose-panel">
              <div className="flex items-center gap-2 mb-1">
                <Badge className="text-[9px] px-1.5" style={{ backgroundColor: composeType === 'need' ? '#ef4444' : '#22c55e', color: '#fff' }}>
                  {composeType === 'need' ? 'NEED' : 'OFFER'}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {composeType === 'need' ? "What do you need?" : "What can you offer?"}
                </span>
              </div>
              <Input
                placeholder={composeType === 'need' ? 'e.g. Need water, food for 3 people' : 'e.g. Have spare batteries, can give lifts'}
                value={composeText}
                onChange={(e) => setComposeText(e.target.value)}
                className="h-8 text-xs bg-card border-border/40"
                data-testid="compose-text-input"
                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              />
              <div className="flex gap-1 flex-wrap">
                {['food', 'water', 'shelter', 'medical', 'charging', 'transport'].map(cat => (
                  <button
                    key={cat}
                    onClick={() => setComposeCategory(composeCategory === cat ? '' : cat)}
                    className={`px-2 py-0.5 text-[10px] rounded-full border transition-colors ${
                      composeCategory === cat
                        ? 'border-transparent text-white'
                        : 'border-border/40 text-muted-foreground hover:text-foreground'
                    }`}
                    style={composeCategory === cat ? { backgroundColor: c1 } : {}}
                  >
                    {cat}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" className="flex-1 h-7 text-xs" onClick={() => setShowCompose(false)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  className="flex-1 h-7 text-xs text-white"
                  style={{ backgroundColor: c1 }}
                  onClick={handleSend}
                  disabled={!composeText.trim() || sending}
                  data-testid="send-message-btn"
                >
                  {sending ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
                  {isOffline ? 'Queue' : 'Broadcast'}
                </Button>
              </div>
            </div>
          )}

          {/* Messages feed */}
          {allMessages.length > 0 && (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Nearby Activity</p>
              {allMessages.slice(0, 20).map(msg => (
                <NearbyMessageCard key={msg.id} msg={msg} color={c1} onNavigate={onNavigate} />
              ))}
            </div>
          )}

          {allMessages.length === 0 && !showCompose && (
            <div className="text-center py-4">
              <Radio className="w-8 h-8 mx-auto mb-2 opacity-30" style={{ color: c1 }} />
              <p className="text-xs text-muted-foreground">No nearby activity yet</p>
              <p className="text-[10px] text-muted-foreground/60 mt-0.5">Post a need or offer to start</p>
            </div>
          )}
        </div>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes radar-pulse {
          0% { transform: scale(0.3); opacity: 0.6; }
          100% { transform: scale(1.2); opacity: 0; }
        }
        @keyframes radar-sweep {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes beacon-blink {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.2); }
        }
      `}</style>
    </div>
  );
}
